/**
 * Resilient, fast LaTeX-to-HTML parser specifically optimized for the Persian
 * book: "ساخت سامانه‌های عامل هوش مصنوعی؛ از معماری تا پیاده‌سازی".
 *
 * Supports line-accurate bidirectional synchronization between Monaco editor
 * and web preview (< 15ms browser rendering).
 */

import katex from "katex";

export interface HeadingItem {
  id: string;
  level: number;
  title: string;
}

export interface ParseResult {
  html: string;
  headings: HeadingItem[];
}

// Basic Python syntax highlighter for minted blocks
function highlightPython(code: string): string {
  const keywords = new Set([
    "def", "class", "import", "from", "return", "if", "elif", "else",
    "for", "while", "in", "not", "and", "or", "try", "except", "finally",
    "with", "as", "raise", "yield", "async", "await", "lambda", "pass",
    "break", "continue", "True", "False", "None", "self", "assert"
  ]);

  const escapeHtml = (str: string) =>
    str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const lines = code.split("\n");
  const highlightedLines = lines.map((line) => {
    let comment = "";
    let codePart = line;
    const commentIdx = line.indexOf("#");
    if (commentIdx !== -1) {
      comment = line.slice(commentIdx);
      codePart = line.slice(0, commentIdx);
    }

    const tokenized = codePart.replace(
      /(["'`]{1,3})((?:\\.|[^\\])*?)\1|\b([a-zA-Z_]\w*)\b|(\d+\.?\d*)/g,
      (match, quote, _strContent, word, num) => {
        if (quote) {
          return `<span class="hl-string">${escapeHtml(match)}</span>`;
        }
        if (word && keywords.has(word)) {
          return `<span class="hl-keyword">${word}</span>`;
        }
        if (num) {
          return `<span class="hl-number">${num}</span>`;
        }
        return escapeHtml(match);
      }
    );

    const commentHtml = comment
      ? `<span class="hl-comment">${escapeHtml(comment)}</span>`
      : "";

    return tokenized + commentHtml;
  });

  return highlightedLines
    .map((line, idx) => `<span class="code-line"><span class="line-num">${idx + 1}</span><span class="line-content">${line || "&nbsp;"}</span></span>`)
    .join("\n");
}

export class LatexBookParser {
  private headings: HeadingItem[] = [];
  private headingCounter = 0;

  public parse(latex: string): ParseResult {
    this.headings = [];
    this.headingCounter = 0;

    if (!latex || !latex.trim()) {
      return { html: "<p class=\"book-empty\">هیچ متنی برای پیش‌نمایش وجود ندارد.</p>", headings: [] };
    }

    const lines = latex.split("\n");
    const htmlBlocks: string[] = [];
    let inDocument = !(/\\begin\{document\}/.test(latex));

    let i = 0;
    while (i < lines.length) {
      const lineNo = i + 1;
      const rawLine = lines[i];

      // Strip comments (% not preceded by backslash)
      const cleanLine = rawLine.replace(/(^|[^\\])%.*$/, "$1").trim();

      // Check document start/end
      if (/\\begin\{document\}/.test(cleanLine)) {
        inDocument = true;
        i++;
        continue;
      }
      if (/\\end\{document\}/.test(cleanLine)) {
        inDocument = false;
        break;
      }

      if (!inDocument) {
        i++;
        continue;
      }

      // Ignore structural book directives
      if (/^\\(frontmatter|mainmatter|backmatter|tableofcontents|listoffigures|listoftables|newpage|clearpage|cleardoublepage)\s*$/.test(cleanLine)) {
        i++;
        continue;
      }

      // Skip blank lines
      if (!cleanLine) {
        i++;
        continue;
      }

      // 1. Environment blocks (\begin{...} ... \end{...})
      const beginEnvMatch = cleanLine.match(/^\\begin\{([a-zA-Z0-9_*+-]+)\}/);
      if (beginEnvMatch) {
        const envName = beginEnvMatch[1];
        const startLine = lineNo;
        const envLines: string[] = [rawLine];
        i++;
        const endPattern = new RegExp(`\\\\end\\{${envName.replace("*", "\\*")}\\}`);
        while (i < lines.length) {
          envLines.push(lines[i]);
          if (endPattern.test(lines[i])) {
            break;
          }
          i++;
        }
        const endLine = Math.min(lines.length, i + 1);
        const envFullText = envLines.join("\n");
        i++;

        const envHtml = this.renderEnvironment(envName, envFullText, startLine, endLine);
        if (envHtml) htmlBlocks.push(envHtml);
        continue;
      }

      // 2. Display math $$ ... $$
      if (cleanLine.startsWith("$$")) {
        const startLine = lineNo;
        const mathLines: string[] = [rawLine];
        i++;
        if (cleanLine === "$$" || (!cleanLine.slice(2).includes("$$"))) {
          while (i < lines.length) {
            mathLines.push(lines[i]);
            if (lines[i].includes("$$")) {
              break;
            }
            i++;
          }
        }
        const endLine = Math.min(lines.length, i + 1);
        i++;
        const mathBody = mathLines.join("\n").replace(/^\$\$/g, "").replace(/\$\$$/g, "");
        htmlBlocks.push(this.renderDisplayMath(mathBody, startLine, endLine));
        continue;
      }

      // 3. Headings — regex supports nested braces (e.g. \section{Title \textbf{bold}})
      const headingMatch = cleanLine.match(/^\\(chapter|section|subsection|subsubsection|paragraph)\*?\{((?:[^{}]|\{[^{}]*\})*)\}/);
      if (headingMatch) {
        const hCmd = headingMatch[1];
        const hTitle = headingMatch[2];
        const levelMap: Record<string, number> = {
          chapter: 1, section: 2, subsection: 3, subsubsection: 4, paragraph: 5,
        };
        const hLevel = levelMap[hCmd] || 2;
        if (hLevel === 5) {
          htmlBlocks.push(`<h5 class="book-paragraph" data-line-start="${lineNo}" data-line-end="${lineNo}" data-line="${lineNo}">${this.parseInline(hTitle)}</h5>`);
        } else {
          htmlBlocks.push(this.createHeading(hTitle, hLevel, lineNo, lineNo));
        }
        i++;
        continue;
      }

      // 4. Contiguous Paragraph: collect lines until a blank line, heading, or environment
      const startLine = lineNo;
      const paraLines: { lineNo: number; text: string }[] = [];

      while (i < lines.length) {
        const curRaw = lines[i];
        const curClean = curRaw.replace(/(^|[^\\])%.*$/, "$1").trim();
        if (!curClean) {
          i++;
          break;
        }
        if (/^\\(begin\{|chapter|section|subsection|subsubsection|paragraph|frontmatter|mainmatter|backmatter|tableofcontents|listoffigures|listoftables|newpage|clearpage|cleardoublepage)/.test(curClean) || curClean.startsWith("$$")) {
          break;
        }

        paraLines.push({ lineNo: i + 1, text: curClean });
        i++;
      }

      // SAFETY: prevent infinite loop — if paragraph collected nothing, the line
      // matched a structural break pattern but no handler above consumed it.
      // Force-advance i and render the line as inline content.
      if (paraLines.length === 0) {
        const fallbackParsed = this.parseInline(cleanLine);
        if (fallbackParsed.trim()) {
          htmlBlocks.push(`<p class="book-para" data-line-start="${lineNo}" data-line-end="${lineNo}"><span class="book-line" data-line="${lineNo}">${fallbackParsed}</span></p>`);
        }
        i++;
        continue;
      }

      const endLine = paraLines[paraLines.length - 1].lineNo;
      const spans = paraLines.map((p) => `<span class="book-line" data-line="${p.lineNo}">${this.parseInline(p.text)}</span>`);
      htmlBlocks.push(`<p class="book-para" data-line-start="${startLine}" data-line-end="${endLine}">${spans.join(" ")}</p>`);
    }

    return {
      html: htmlBlocks.join("\n\n"),
      headings: this.headings,
    };
  }

  private renderEnvironment(envName: string, fullText: string, startLine: number, endLine: number): string {
    // Extract inner body between \begin{...} and \end{...}
    const innerBodyMatch = fullText.match(/^\\begin\{[^{}]+\}(?:\[.*?\])?(?:\{.*?\})?([\s\S]*?)\\end\{[^{}]+\}$/);
    const body = innerBodyMatch ? innerBodyMatch[1] : fullText;

    if (envName === "minted") {
      const langMatch = fullText.match(/^\\begin\{minted\}(?:\[.*?\])?\{([a-zA-Z0-9_+-]+)\}/);
      const lang = langMatch ? langMatch[1] : "text";
      const highlighted = highlightPython(body.trim());
      return `<div class="book-code-block" data-line-start="${startLine}" data-line-end="${endLine}" dir="ltr"><div class="code-header"><span class="code-lang">${lang}</span></div><pre><code>${highlighted}</code></pre></div>`;
    }

    if (envName === "ltrcode") {
      return `<div class="book-ltr-container" data-line-start="${startLine}" data-line-end="${endLine}" dir="ltr">${this.parseInline(body)}</div>`;
    }

    if (envName === "ltrexample") {
      return `<div class="book-ltr-example" data-line-start="${startLine}" data-line-end="${endLine}" dir="ltr">${this.parseInline(body)}</div>`;
    }

    if (/^(equation\*?|align\*?|gather\*?)$/.test(envName)) {
      return this.renderDisplayMath(body.trim(), startLine, endLine);
    }

    if (/^(tabular\*?|longtable)$/.test(envName)) {
      return this.parseTable(body, startLine, endLine);
    }

    if (/^figure\*?$/.test(envName)) {
      return this.parseFigure(body, startLine, endLine);
    }

    if (envName === "itemize") {
      return this.parseList(body, "ul", startLine, endLine);
    }

    if (envName === "enumerate") {
      return this.parseList(body, "ol", startLine, endLine);
    }

    // Generic fallback for any other environment
    return `<div class="book-env book-env-${envName}" data-line-start="${startLine}" data-line-end="${endLine}">${this.parseInline(body)}</div>`;
  }

  private renderDisplayMath(math: string, startLine: number, endLine: number): string {
    try {
      const rendered = katex.renderToString(math.trim(), {
        displayMode: true,
        throwOnError: false,
      });
      return `<div class="book-math-block" data-line-start="${startLine}" data-line-end="${endLine}" dir="ltr">${rendered}</div>`;
    } catch {
      return `<div class="book-math-block book-math-fallback" data-line-start="${startLine}" data-line-end="${endLine}" dir="ltr"><code>${math}</code></div>`;
    }
  }

  private createHeading(rawTitle: string, level: number, startLine: number, endLine: number): string {
    const id = `heading-${++this.headingCounter}`;
    const cleanText = rawTitle.replace(/\\(?:textbf|textit|emph|LR|RL)\{([^{}]+)\}/g, "$1").trim();
    this.headings.push({ id, level, title: cleanText });

    const innerHtml = this.parseInline(rawTitle);
    return `<h${level} id="${id}" class="book-h${level}" data-line-start="${startLine}" data-line-end="${endLine}" data-line="${startLine}">${innerHtml}</h${level}>`;
  }

  private parseFigure(body: string, startLine: number, endLine: number): string {
    let imgHtml = "";
    const imgMatch = body.match(/\\includegraphics(?:\[.*?\])?\{([^{}]+)\}/);
    if (imgMatch) {
      const imgPath = imgMatch[1].trim();
      const rawUrl = `/api/files/raw?path=${encodeURIComponent(imgPath)}`;
      imgHtml = `<img class="book-figure-img" src="${rawUrl}" alt="${imgPath}" loading="lazy" />`;
    }

    let captionHtml = "";
    const captionMatch = body.match(/\\caption\{([^{}]+)\}/);
    if (captionMatch) {
      captionHtml = `<figcaption class="book-figure-caption">${this.parseInline(captionMatch[1])}</figcaption>`;
    }

    return `<div class="book-figure-wrapper" data-line-start="${startLine}" data-line-end="${endLine}"><figure class="book-figure">${imgHtml}${captionHtml}</figure></div>`;
  }

  private parseList(body: string, tag: "ul" | "ol", startLine: number, endLine: number): string {
    const items = body.split(/\\item\b/);
    const listItems = items
      .slice(1)
      .map((item) => {
        const itemContent = this.parseInline(item.trim());
        return `<li class="book-list-item">${itemContent}</li>`;
      })
      .join("\n");

    return `<div class="book-list-wrapper" data-line-start="${startLine}" data-line-end="${endLine}"><${tag} class="book-${tag}">${listItems}</${tag}></div>`;
  }

  private parseTable(body: string, startLine: number, endLine: number): string {
    const cleanBody = body.replace(/\\(toprule|midrule|bottomrule|hline|rowcolor\{.*?\})/g, "");
    const rows = cleanBody.split(/\\\\/);

    const tableRows = rows
      .map((row) => row.trim())
      .filter((row) => row.length > 0)
      .map((row, idx) => {
        const cells = row.split("&");
        const cellTag = idx === 0 ? "th" : "td";
        const cellsHtml = cells
          .map((cell) => `<${cellTag} class="book-table-cell">${this.parseInline(cell.trim())}</${cellTag}>`)
          .join("");
        return `<tr>${cellsHtml}</tr>`;
      })
      .join("\n");

    return `<div class="book-table-wrapper" data-line-start="${startLine}" data-line-end="${endLine}"><table class="book-table"><tbody>${tableRows}</tbody></table></div>`;
  }

  public parseInline(text: string): string {
    let s = text;

    // 1. Custom Book command: \enterm{Persian}{English}
    s = s.replace(
      /\\enterm\{([^{}]+)\}\{([^{}]+)\}/g,
      (_, fa, en) =>
        `<span class="book-enterm">${this.parseInline(fa)} <span class="enterm-badge" dir="ltr">${en}</span></span>`
    );

    // 2. Custom Book command: \trnote{...}
    s = s.replace(
      /\\trnote\{([^{}]+)\}/g,
      (_, note) =>
        `<aside class="book-trnote" dir="rtl"><span class="trnote-label">[یادداشت مترجم]</span> ${this.parseInline(note)}</aside>`
    );

    // 3. BiDi & Language: \LR{...}, \RL{...}
    s = s.replace(/\\LR\{([^{}]+)\}/g, (_, inner) => `<span class="book-lr" dir="ltr">${this.parseInline(inner)}</span>`);
    s = s.replace(/\\RL\{([^{}]+)\}/g, (_, inner) => `<span class="book-rl" dir="rtl">${this.parseInline(inner)}</span>`);

    // 4. Inline Math: $...$
    s = s.replace(/\$([^\$\n]+)\$/g, (_, math) => {
      try {
        return katex.renderToString(math.trim(), {
          displayMode: false,
          throwOnError: false,
        });
      } catch {
        return `<code class="book-math-inline">${math}</code>`;
      }
    });

    // 5. Formatting
    s = s.replace(/\\textbf\{([^{}]+)\}/g, (_, inner) => `<strong>${this.parseInline(inner)}</strong>`);
    s = s.replace(/\\textit\{([^{}]+)\}/g, (_, inner) => `<em>${this.parseInline(inner)}</em>`);
    s = s.replace(/\\texttt\{([^{}]+)\}/g, (_, inner) => `<code class="book-inline-code" dir="ltr">${inner}</code>`);
    s = s.replace(/\\emph\{([^{}]+)\}/g, (_, inner) => `<em>${this.parseInline(inner)}</em>`);
    s = s.replace(/\\underline\{([^{}]+)\}/g, (_, inner) => `<u>${this.parseInline(inner)}</u>`);
    s = s.replace(/\\textcolor\{([^{}]+)\}\{([^{}]+)\}/g, (_, color, inner) => `<span style="color: ${color}">${this.parseInline(inner)}</span>`);

    // 6. Footnotes
    s = s.replace(/\\LTRfootnote\{([^{}]+)\}/g, (_, fn) => `<sup class="book-fn" dir="ltr" title="${fn}">[${fn}]</sup>`);
    s = s.replace(/\\footnote\{([^{}]+)\}/g, (_, fn) => `<sup class="book-fn" title="${fn}">[پاورقی]</sup>`);

    // 7. References and Links
    s = s.replace(/\\href\{([^{}]+)\}\{([^{}]+)\}/g, (_, url, label) => `<a href="${url}" class="book-link" target="_blank" rel="noopener noreferrer">${this.parseInline(label)}</a>`);
    s = s.replace(/\\url\{([^{}]+)\}/g, (_, url) => `<a href="${url}" class="book-link" target="_blank" rel="noopener noreferrer">${url}</a>`);
    s = s.replace(/\\cite\{([^{}]+)\}/g, (_, key) => `<span class="book-cite">[${key}]</span>`);
    s = s.replace(/\\ref\{([^{}]+)\}/g, (_, key) => `<span class="book-ref">${key}</span>`);

    // 8. Raw Includegraphics when outside figure
    s = s.replace(
      /\\includegraphics(?:\[.*?\])?\{([^{}]+)\}/g,
      (_, imgPath) => `<img class="book-inline-img" src="/api/files/raw?path=${encodeURIComponent(imgPath)}" alt="${imgPath}" />`
    );

    // 9. Common typographical symbols
    s = s.replace(/---/g, "—");
    s = s.replace(/--/g, "–");
    s = s.replace(/\\textemdash/g, "—");
    s = s.replace(/\\textquotesingle/g, "'");
    s = s.replace(/\\textless/g, "&lt;");
    s = s.replace(/\\textgreater/g, "&gt;");
    s = s.replace(/\\\\/g, "<br />");

    // 10. Fallback: Strip unknown single commands like \somecmd
    s = s.replace(/\\[a-zA-Z@]+(?:\*|\b)/g, "");

    return s;
  }
}

export const bookParser = new LatexBookParser();
